import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { pipelinePermissionsApi } from '../lib/api.js';
import { queryKeys } from '../lib/query-keys.js';
import type { SavePipelinePermissionsRequest } from '../types/api.js';

/**
 * pipeline_permissions/default 取得フック
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business pipeline 管理権限ドキュメント取得フック。TanStack Query でキャッシュ管理
 * @graph-connects pipelinePermissionsApi [calls] API クライアント経由で取得
 * @graph-connects queryKeys [calls] TanStack Query キー生成
 */
export function usePipelinePermissions() {
  return useQuery({
    queryKey: queryKeys.pipelinePermissions.current(),
    queryFn: pipelinePermissionsApi.get,
    // 権限ドキュメントは頻繁に変わらないので 30 秒 stale 許容
    staleTime: 30_000,
  });
}

/**
 * pipeline_permissions/default 保存フック
 *
 * 保存成功時に取得キャッシュを無効化する。
 *
 * @graph-stack cortex-admin
 * @graph-domain Infrastructure
 * @graph-business pipeline 管理権限保存ミューテーション。保存後にキャッシュを invalidate
 * @graph-connects pipelinePermissionsApi [calls] API クライアント経由で保存
 * @graph-connects queryKeys [calls] TanStack Query キー生成・キャッシュ無効化
 */
export function useSavePipelinePermissions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: SavePipelinePermissionsRequest) => pipelinePermissionsApi.save(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.pipelinePermissions.all });
    },
  });
}
